/**
 * Mix & match picker (blocks/mix-and-match.liquid)
 *
 * - Customer picks N tins (set size comes from the block setting).
 * - Picks are written to hidden line item property inputs tied to the
 *   product form via the form="" attribute: properties[Tin 1], [Tin 2], ...
 * - Picks are written in the order the tins appear on the page, so the same
 *   combination always produces identical properties and merges into one cart line.
 * - A hidden required "guard" input blocks form submission until the set is full.
 */

const ESCAPE = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (c) => ESCAPE[c]);

class MixAndMatchComponent extends HTMLElement {
  /** @type {string[]} names of picked tins, in the order they were picked */
  picks = [];

  connectedCallback() {
    this.setSize = Number(this.dataset.setSize) || 3;
    this.allowDuplicates = this.dataset.allowDuplicates === 'true';

    this.tins = Array.from(this.querySelectorAll('[data-mm-tin]'));
    this.tinOrder = this.tins.map((tin) => tin.dataset.name);
    this.slots = Array.from(this.querySelectorAll('[data-mm-slot]'));
    this.inputs = Array.from(this.querySelectorAll('[data-mm-input]'));
    this.guard = this.querySelector('[data-mm-guard]');
    this.progressEl = this.querySelector('[data-mm-progress]');
    this.barEl = this.querySelector('[data-mm-bar]');
    this.errorEl = this.querySelector('[data-mm-error]');
    this.clearBtn = this.querySelector('[data-mm-clear]');
    this.ctaBtn = this.querySelector('[data-mm-cta]');

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.addEventListener('click', this.onClick, { signal });

    // Browser fires "invalid" on the guard when someone tries to add an incomplete set.
    this.guard?.addEventListener(
      'invalid',
      (event) => {
        event.preventDefault(); // suppress the native tooltip, show ours instead
        this.showError();
      },
      { signal }
    );

    // Reset after a successful add so the next set starts fresh.
    document.addEventListener('cart:update', this.onCartUpdate, { signal });

    // Request image files big enough for the card size, crop and zoom (keeps tin labels sharp).
    this.zoom = Math.max(1, (Number(this.dataset.zoom) || 100) / 100);
    this.images = Array.from(this.querySelectorAll('img.mm-tin__image'));
    this.images.forEach((img) => img.addEventListener('load', () => this.sizeImage(img), { once: true, signal }));
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.sizeImages());
      this.resizeObserver.observe(this);
    }
    this.sizeImages();

    this.render();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
  }

  sizeImages() {
    this.images?.forEach((img) => this.sizeImage(img));
  }

  /** @param {HTMLImageElement} img */
  sizeImage(img) {
    const box = img.parentElement;
    if (!box) return;
    const boxW = box.clientWidth;
    const boxH = box.clientHeight;
    if (!boxW || !boxH) return;

    const imgW = img.naturalWidth || Number(img.getAttribute('width')) || 1;
    const imgH = img.naturalHeight || Number(img.getAttribute('height')) || 1;
    const photoRatio = imgW / imgH;
    const boxRatio = boxW / boxH;

    // object-fit: cover scales a wider photo by its height, so it needs extra width
    const cropBoost = Math.max(photoRatio / boxRatio, 1);
    const needed = Math.ceil(boxW * cropBoost * this.zoom);
    const sizes = `${needed}px`;
    if (img.sizes !== sizes) img.sizes = sizes;
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const addBtn = target.closest('[data-mm-add]');
    if (addBtn) {
      const tin = addBtn.closest('[data-mm-tin]');
      if (tin) this.add(tin.dataset.name);
      return;
    }

    const removeBtn = target.closest('[data-mm-remove]');
    if (removeBtn) {
      const tin = removeBtn.closest('[data-mm-tin]');
      if (tin) this.removeOne(tin.dataset.name);
      return;
    }

    const slotRemove = target.closest('[data-mm-slot-remove]');
    if (slotRemove) {
      this.removeAt(Number(slotRemove.dataset.index));
      return;
    }

    if (target.closest('[data-mm-cta]')) {
      this.addToCart();
      return;
    }

    if (target.closest('[data-mm-clear]')) {
      this.picks = [];
      this.render();
    }
  };

  /** @param {CustomEvent} event */
  onCartUpdate = (event) => {
    const data = event.detail?.data;
    if (!data || data.source !== 'product-form-component' || data.didError) return;
    if (String(data.productId) !== String(this.dataset.productId)) return;
    if (this.picks.length !== this.setSize) return;

    this.picks = [];
    this.render();
    if (this.progressEl && this.dataset.msgAdded) {
      this.progressEl.textContent = this.dataset.msgAdded;
    }
  };

  countOf(name) {
    return this.picks.filter((pick) => pick === name).length;
  }

  maxPerTin() {
    return this.allowDuplicates ? this.setSize : 1;
  }

  add(name) {
    if (!name) return;
    if (this.picks.length >= this.setSize) return;
    if (this.countOf(name) >= this.maxPerTin()) return;
    this.picks.push(name);
    this.render();
  }

  removeOne(name) {
    const index = this.picks.lastIndexOf(name);
    if (index > -1) this.removeAt(index);
  }

  removeAt(index) {
    if (index < 0 || index >= this.picks.length) return;
    this.picks.splice(index, 1);
    this.render();
  }

  /** Picks sorted by on-page tin order, so identical sets merge in the cart. */
  sortedPicks() {
    return [...this.picks].sort((a, b) => this.tinOrder.indexOf(a) - this.tinOrder.indexOf(b));
  }

  /** Clicks the theme's real Add to cart button, so all its normal behavior runs. */
  addToCart() {
    const form = this.guard?.form;
    const realButton = form?.querySelector('button[name="add"]');
    if (realButton && !realButton.disabled) {
      realButton.click();
    } else if (form) {
      form.requestSubmit();
    }
  }

  showError() {
    if (!this.errorEl) return;
    const remaining = this.setSize - this.picks.length;
    const template = this.dataset.msgError || 'Choose [remaining] more to complete your set.';
    this.errorEl.textContent = template.replace('[remaining]', String(remaining));
    this.errorEl.hidden = false;

    this.classList.remove('is-shaking');
    void this.offsetWidth; // restart animation
    this.classList.add('is-shaking');

    const rect = this.getBoundingClientRect();
    if (rect.top < 0 || rect.top > window.innerHeight * 0.6) {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  render() {
    const count = this.picks.length;
    const full = count >= this.setSize;
    const maxPerTin = this.maxPerTin();

    // Tin cards
    for (const tin of this.tins) {
      const name = tin.dataset.name;
      const n = this.countOf(name);
      const unavailable = tin.hasAttribute('data-unavailable');

      tin.classList.toggle('is-selected', n > 0);
      const badge = tin.querySelector('[data-mm-count]');
      if (badge) badge.textContent = n > 1 ? `×${n}` : n === 1 ? '✓' : '';

      const addBtn = tin.querySelector('[data-mm-add]');
      if (addBtn) addBtn.disabled = unavailable || full || n >= maxPerTin;

      const removeBtn = tin.querySelector('[data-mm-remove]');
      if (removeBtn) removeBtn.disabled = n === 0;
    }

    // Tray slots (in pick order, so customers see what they just added)
    this.slots.forEach((slot, i) => {
      const name = this.picks[i];
      slot.classList.toggle('is-filled', Boolean(name));

      if (!name) {
        slot.innerHTML = `<span class="mix-match__slot-number" aria-hidden="true">${i + 1}</span><span class="visually-hidden">Slot ${i + 1}: empty</span>`;
        return;
      }

      const tin = this.tins.find((t) => t.dataset.name === name);
      const img = tin?.dataset.image
        ? `<img src="${escapeHtml(tin.dataset.image)}" alt="" loading="lazy" width="160" height="160">`
        : '';

      slot.innerHTML = `${img}<span class="mix-match__slot-name">${escapeHtml(name)}</span><button type="button" class="mix-match__slot-remove" data-mm-slot-remove data-index="${i}" aria-label="Remove ${escapeHtml(name)} from slot ${i + 1}">&times;</button>`;
    });

    // Hidden line item property inputs
    const sorted = this.sortedPicks();
    this.inputs.forEach((input, i) => {
      input.value = sorted[i] || '';
    });

    // Validation guard
    if (this.guard) this.guard.value = full ? 'complete' : '';

    // Progress copy + bar
    if (this.progressEl) {
      const progressTemplate = this.dataset.msgProgress || '[count] of [total] chosen';
      this.progressEl.textContent = full
        ? this.dataset.msgComplete || 'Your set is ready'
        : progressTemplate.replace('[count]', String(count)).replace('[total]', String(this.setSize));
    }
    if (this.barEl) this.barEl.style.width = `${(count / this.setSize) * 100}%`;
    if (this.clearBtn) this.clearBtn.hidden = count === 0;
    if (this.ctaBtn) this.ctaBtn.hidden = !full;

    // Hide the error once the set is complete (or update the number if it's showing)
    if (this.errorEl && !this.errorEl.hidden) {
      if (full) this.errorEl.hidden = true;
      else this.showErrorText();
    }
  }

  showErrorText() {
    const remaining = this.setSize - this.picks.length;
    const template = this.dataset.msgError || 'Choose [remaining] more to complete your set.';
    this.errorEl.textContent = template.replace('[remaining]', String(remaining));
  }
}

if (!customElements.get('mix-and-match-component')) {
  customElements.define('mix-and-match-component', MixAndMatchComponent);
}
